// Firebase products enabled (https://firebase.google.com/docs/web/setup#available-libraries)
import { initializeApp } from "firebase/app";
import { getAnalytics } from "firebase/analytics";
import { getAuth, onAuthStateChanged, signOut, signInWithEmailAndPassword} from "firebase/auth";
import { getFirestore, collection, getDocs, setDoc, doc, deleteDoc } from "@firebase/firestore";
import { useEffect, useState } from "react";
import { GameObject, ACNH_Element } from "./class-types";
import { createTuple, parseID, handleError } from "scripts/FunctionsBundle";

// ############################ LOCAL TYPES ############################
type _User = import('firebase/auth').User;
type _Capturapedia = import('./class-types').Capturapedia;

/* ##### FIREBASE CONFIGURATION (READ ME) #####
  
*** DISCLAIMER: ALTHOUGH YOU CAN ACCESS THE FIREBASE PROYECT, YOU'LL NOT BE ABLE TO WRITE, DELETE OR MODIFY
ANYTHING WITHOUT ADMIN PERMISSONS (WHICH ARE SECRET). THIS IS (AND WILL EVER BE) AN OPEN SOURCE PROYECT, SO
FEEL FREE TO CONECT AND READ DIRECTLY FROM THE FIRESTORE OR =THE-GAME-CODEX-API= (WIP) ***

*/
const firebaseConfig = {
  apiKey: "AIzaSyBFk0qjtZyGBs2SMLndzpPG1iOEezjxSRU",
  authDomain: "the-game-codex101016.firebaseapp.com",
  projectId: "the-game-codex101016",
  storageBucket: "the-game-codex101016.appspot.com",
  messagingSenderId: "38716611746",
  appId: "1:38716611746:web:09fd00068f22eda4298bfb",
  measurementId: "G-599JW5D1H7",
};

// Initialize Firebase
const firebaseApp = initializeApp(firebaseConfig);
export const analytics = getAnalytics(firebaseApp);
export const auth = getAuth(firebaseApp);
export const firestore = getFirestore(firebaseApp);

// Quick references
const GamesCollection = collection(firestore, "games");

// --------------------------------------------------------------------------------------------
// eslint-disable-next-line @typescript-eslint/no-unused-expressions
(""); /* ############################ FIRESTORE - QUICK FETCH ############################
----------------------------------------------------------------------------------------------- */

// ############################ QUICK GAMES LIST FETCH ############################
/**
 * Fetch data from `Firestore` initially to get the `GamesList` object.
 * @see {@link GameObject}
 * @returns A tuple containing the Games List object and a function to refresh it
 */
export function useGamesList() {
  const [GamesList, setGamesList] = useState(Array<GameObject>());

  function update() {
    console.log("Lectura GAMES LIST");
    getDocs(GamesCollection).then((snap) => {
    var aux: Array<GameObject> = [];

    snap.docs.forEach((doc) => {
      const Game = new GameObject(doc.data());
      Game.link = doc.id;
      aux.push(Game);
    });

    setGamesList(aux);
    })
  }

  // Fetch once at start
  useEffect(update, []);

  return createTuple([GamesList, update]);
}

// ############################ QUICK CAPTURAPEDIA FETCH ############################
/**
 * Fetch data from `Firestore` initially to get the `Capturapedia` object.
 * @see {@link Capturapedia}
 * @type Capturapedia
 * @returns A tuple containing the Capturapedia object and a function to refresh it
 */
export function useCapturapedia() {
  const [Capturapedia, setCapturapedia] = useState<_Capturapedia>({});

  function update() {
    console.log("Lectura CAPTURAPEDIA");
    Object.keys(ACNH_Element.classes).forEach((key) => {
      getDocs(collection(firestore, "games", "animal-crossing-new-horizons", key))
      .then((snap) => {
        const {specifics: _, ...struct} = new ACNH_Element.classes[key]();

        var aux: Array<typeof struct> = [];

        snap.docs.forEach((doc) => {
          aux.push(doc.data());
        });

        setCapturapedia((prev) => ({...prev, [key]: aux}));
      });
    });
  }

  // Fetch once at start
  useEffect(update, []);

  return createTuple([Capturapedia, update]);
};

// --------------------------------------------------------------------------------------------
// eslint-disable-next-line @typescript-eslint/no-unused-expressions
(""); /* ############################ FIRESTORE - GENERALS ############################
----------------------------------------------------------------------------------------------- */

// ############################ ADD FIREBASE ELEMENT ############################
/**
 * Updates an document from the `Firestore`. Every parameter is converted into `parseID`
 * @see {@link parseID} To search within the Firestore database
 * @param gameDOC Game to select
 * @param categoryCOL Game's sub-collection to select
 * @param itemDOC Specific document to update/create
 * @returns A Promise resolved once the document has been successfully updated from the backend (note that it won't resolve while you're offline).
 */
export function addElement(gameDOC: string, categoryCOL: string, itemDOC: string, data: any) {
  gameDOC = parseID(gameDOC);
  categoryCOL = parseID(categoryCOL);
  itemDOC = parseID(itemDOC);
  
  const coll = doc(firestore, "games", gameDOC, categoryCOL, itemDOC);

  return setDoc(coll, { ...data});
}

// ############################ DELETE FIREBASE ELEMENT ############################
/**
 * Deletes an document from the `Firestore`. Every parameter is converted into `parseID`
 * @see {@link parseID} To search within the Firestore database
 * @param gameDOC Game to select
 * @param categoryCOL Game's sub-collection to select
 * @param itemDOC Specific document to delete
 * @returns A Promise resolved once the document has been successfully deleted from the backend (note that it won't resolve while you're offline).
 */
export function delElement(gameDOC: string, categoryCOL: string, itemDOC: string) {
  gameDOC = parseID(gameDOC);
  categoryCOL = parseID(categoryCOL);
  itemDOC = parseID(itemDOC);

  const coll = doc(firestore, "games", gameDOC, categoryCOL, itemDOC);

  return deleteDoc(coll);
}

// --------------------------------------------------------------------------------------------
// eslint-disable-next-line @typescript-eslint/no-unused-expressions
(""); /* ############################ AUTHENTICATION - ADMIN USE ONLY ############################
----------------------------------------------------------------------------------------------- */

// ############################ REGISTER NEW USER ############################
/**
 * Registers, and logs in a new `User` with the `email` and `password` provided.
 * @param email User's email address.
 * @param password User's chosen password to use.
 * @depreceated ***This function is not enabled.***
 */
export function register(email: string, password: string) {
  // createUserWithEmailAndPassword(auth, email, password).then((user) => {
  //   if (!user.user.emailVerified) {
  //     sendEmailVerification(user.user).then(() => {
  //       window.alert("Email verification sent to: " + user.user.email)
  //     }).catch(handleError);
  //   }
  // }).catch(handleError);
}

// ############################ LOG IN USER ############################
/**
 * Logs in that `User` from the specified `email` and `password`.
 * @param email User's email address.
 * @param password User's chosen password registered to that email address. This **IS NOT**
 * the same password to access that email account.
 */
export function login(email: string, password: string) {
  signInWithEmailAndPassword(auth, email, password).then((user) => {
    window.alert("Welcome " + user.user.email);
  }).catch(handleError);
}

// ############################ LOG OUT USER ############################
/**
 * Logs out any active user.
 */
export function logout() {
  signOut(auth).then(() => {
    window.alert("Logged out");
  }).catch(handleError);
}

// ############################ CURRENT USER ############################
/**
 * Gets the current user from `auth`
 * @returns The `User` logged in, or null if its none.
 */
export function useCurrentUser() {
  const [currentUser, setCurrentUser] = useState<_User | null>(null);
  onAuthStateChanged(auth, (current) => {
    setCurrentUser(current)
  });
  return currentUser;
};
